import React, { useState, useEffect } from 'react';
import api from './api';
import PioneerBadge from './PioneerBadge';
import AchillesBadge from './AchillesBadge';

const PublicProfile = ({ userId, onBack, currentUserId }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [tags, setTags] = useState([]);
  const [results, setResults] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [clubs, setClubs] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileRes, followRes, tagsRes, resultsRes, sponsorsRes, clubsRes] = await Promise.all([
          api.get(`/auth/users/${userId}`),
          api.get(`/follows/check/${userId}`),
          api.get(`/tags/user/${userId}`),
          api.get(`/results/user/${userId}`),
          api.get(`/sponsors/user/${userId}`),
          api.get(`/clubs/user/${userId}`),
        ]);
        setProfile(profileRes.data);
        setIsFollowing(followRes.data.following);
        setTags(tagsRes.data);
        setResults(resultsRes.data);
        setSponsors(sponsorsRes.data);
        setClubs(clubsRes.data);
      } catch (err) {
        console.error('Error loading profile:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId]);

  const handleToggleFollow = async () => {
    try {
      if (isFollowing) {
        await api.delete(`/follows/${userId}`);
        setIsFollowing(false);
        setProfile(p => p ? { ...p, followers_count: p.followers_count - 1 } : p);
      } else {
        await api.post(`/follows/${userId}`);
        setIsFollowing(true);
        setProfile(p => p ? { ...p, followers_count: p.followers_count + 1 } : p);
      }
    } catch (err) {
      console.error('Toggle follow error:', err);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0B0B10', color: '#FFFFFF', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <p style={{ color: '#8b9cb3' }}>Loading profile...</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={{ minHeight: '100vh', background: '#0B0B10', color: '#FFFFFF', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#8b9cb3' }}>User not found</p>
          <button onClick={onBack} style={{ marginTop: '1rem', padding: '0.5rem 1.5rem', background: '#FF8A3D', color: '#0B0B10', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>Go Back</button>
        </div>
      </div>
    );
  }

  const displayName = profile.first_name
    ? `${profile.first_name} ${profile.last_name || ''}`.trim()
    : profile.name || 'Runner';

  const initials = profile.first_name
    ? (profile.first_name[0] + (profile.last_name?.[0] || '')).toUpperCase()
    : (profile.name || 'R').substring(0, 2).toUpperCase();

  const isSelf = currentUserId === userId;

  return (
    <div style={{ minHeight: '100vh', background: '#0B0B10', color: '#FFFFFF' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0B0B10 0%, #16161D 100%)',
        borderBottom: '1px solid rgba(255, 138, 61, 0.2)',
        padding: '1rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: '#FF8A3D', fontSize: '1.5rem', cursor: 'pointer', padding: '0.25rem' }}>←</button>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Profile</h1>
      </div>

      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '2rem 1.5rem' }}>
        {/* Profile Card */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            overflow: 'hidden',
            border: '3px solid #FF8A3D',
            margin: '0 auto 1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: profile.profile_photo ? 'transparent' : '#16161D',
            fontSize: '2rem',
            fontWeight: 700,
            color: '#FF8A3D',
          }}>
            {profile.profile_photo ? (
              <img src={profile.profile_photo} alt={`${profile.first_name || profile.name || 'Runner'} avatar`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              initials
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: '1.5rem', margin: 0 }}>{displayName}</h2>
            {profile.verified_athlete && (
              <span style={{ color: '#4ade80', fontSize: '0.8rem', fontWeight: 600, background: 'rgba(74, 222, 128, 0.15)', padding: '0.15rem 0.5rem', borderRadius: '10px' }}>✓ Verified</span>
            )}
            <PioneerBadge isPioneer={profile.is_pioneer} pioneerNumber={profile.pioneer_number} isCommunityBuilder={profile.is_community_builder} />
          </div>

          {/* Tags */}
          {tags.length > 0 && (
            <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
              {tags.map(tag => (
                tag === 'achilles-guide' || tag === 'achilles-athlete' ? (
                  <AchillesBadge key={tag} type={tag === 'achilles-guide' ? 'guide' : 'athlete'} />
                ) : (
                  <span key={tag} style={{
                    fontSize: '0.8rem',
                    color: '#FF8A3D',
                    background: 'rgba(255, 138, 61, 0.12)',
                    padding: '0.2rem 0.7rem',
                    borderRadius: '12px',
                    fontWeight: 500,
                  }}>
                    {tag}
                  </span>
                )
              ))}
            </div>
          )}

          {/* Follow counts */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginBottom: '1.5rem' }}>
            <div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{profile.followers_count}</div>
              <div style={{ fontSize: '0.8rem', color: '#8b9cb3' }}>Followers</div>
            </div>
            <div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{profile.following_count}</div>
              <div style={{ fontSize: '0.8rem', color: '#8b9cb3' }}>Following</div>
            </div>
          </div>

          {/* Follow button */}
          {!isSelf && (
            <button
              onClick={handleToggleFollow}
              style={{
                padding: '0.6rem 2rem',
                background: isFollowing ? '#FF8A3D' : 'transparent',
                border: isFollowing ? 'none' : '2px solid #FF8A3D',
                color: isFollowing ? '#0B0B10' : '#FF8A3D',
                borderRadius: '25px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 600,
                transition: 'all 0.2s ease',
              }}
            >
              {isFollowing ? 'Following' : 'Follow'}
            </button>
          )}
        </div>

        {/* Race Results */}
        {results.length > 0 && (
          <div style={{
            background: '#16161D',
            borderRadius: '16px',
            padding: '1.5rem',
            border: '1px solid rgba(255, 138, 61, 0.1)',
          }}>
            <h3 style={{ fontSize: '0.85rem', color: '#8b9cb3', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 1rem 0' }}>
              Race Results
            </h3>
            {results.map((r, i) => (
              <div
                key={r.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.75rem 0',
                  borderTop: i > 0 ? '1px solid rgba(139, 156, 179, 0.1)' : 'none',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, marginBottom: '0.2rem' }}>{r.race_name}</div>
                  <div style={{ fontSize: '0.8rem', color: '#8b9cb3' }}>
                    {r.race_date ? new Date(r.race_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                    {r.distance ? ` · ${r.distance} ${r.distance_unit}` : ''}
                  </div>
                </div>
                <div>
                  {r.status === 'dnf' ? (
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#ff6b6b', background: 'rgba(255, 107, 107, 0.15)', padding: '0.15rem 0.5rem', borderRadius: '8px' }}>DNF</span>
                  ) : r.status === 'dns' ? (
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#8b9cb3', background: 'rgba(139, 156, 179, 0.15)', padding: '0.15rem 0.5rem', borderRadius: '8px' }}>DNS</span>
                  ) : r.finish_time ? (
                    <span style={{ fontFamily: 'monospace', fontSize: '0.95rem', color: '#FF8A3D', fontWeight: 600 }}>{r.finish_time}</span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Sponsors */}
        {sponsors.length > 0 && (
          <div style={{
            background: '#16161D',
            borderRadius: '16px',
            padding: '1.5rem',
            marginTop: '1.5rem',
            border: '1px solid rgba(255, 138, 61, 0.1)',
          }}>
            <h3 style={{ fontSize: '0.85rem', color: '#8b9cb3', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 1rem 0' }}>
              Sponsors
            </h3>
            {sponsors.map((s, i) => (
              <div key={s.id} style={{ padding: '0.75rem 0', borderTop: i > 0 ? '1px solid rgba(139,156,179,0.1)' : 'none' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.2rem' }}>{s.name}</div>
                {s.website && <a href={s.website.startsWith('http') ? s.website : `https://${s.website}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.8rem', color: '#FF8A3D', textDecoration: 'none' }}>{s.website}</a>}
              </div>
            ))}
          </div>
        )}

        {/* Running Clubs */}
        {clubs.length > 0 && (
          <div style={{
            background: '#16161D',
            borderRadius: '16px',
            padding: '1.5rem',
            marginTop: '1.5rem',
            border: '1px solid rgba(255, 138, 61, 0.1)',
          }}>
            <h3 style={{ fontSize: '0.85rem', color: '#8b9cb3', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 1rem 0' }}>
              Running Clubs
            </h3>
            {clubs.map((c, i) => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 0', borderTop: i > 0 ? '1px solid rgba(139,156,179,0.1)' : 'none' }}>
                <div style={{ fontWeight: 600 }}>{c.name}</div>
                <span style={{ fontSize: '0.75rem', color: '#FF8A3D', background: 'rgba(255,138,61,0.12)', padding: '0.15rem 0.5rem', borderRadius: '10px', fontWeight: 500 }}>{c.role}</span>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default PublicProfile;
